/**
 * モックレスポンスを管理するファイル
 *
 * このファイルでは、APIエンドポイントごとのモックレスポンスを定義します。
 * キーはAPIのURLパス、値はレスポンスデータです。
 */

// モック用の画像URL
// 直接アクセス可能でCORS制限のない画像URLに変更
const MOCK_IMAGE_URL = "https://picsum.photos/640/480";

// モックレスポンスのマップ
export const mockResponses: Record<string, any> = {
  // REST APIチェック
  "/api/rest/rest-check/": {
    message: "REST API is working (Mock)",
  },

  // ユーザー関連
  "/api/current-user/login/": {
    id: 1,
    username: "mockuser",
    email: "mock@example.com",
    is_staff: false,
    is_active: true,
  },

  "/api/current-user/logout/": {
    detail: "Successfully logged out",
  },

  "/api/users/": [
    {
      id: 1,
      username: "mockuser1",
      email: "mock1@example.com",
    },
    {
      id: 2,
      username: "mockuser2",
      email: "mock2@example.com",
    },
  ],

  // サイト設定
  "/api/site-conf/": {
    site_name: "モックサイト",
    site_description: "これはモックサイトの説明です",
    contact_email: "mock@example.com",
  },

  // 家具除去関連
  "/item-removal/process": {
    id: 123,
    status: "success",
    processed_image_url: MOCK_IMAGE_URL,
    image_url: MOCK_IMAGE_URL,
    file: MOCK_IMAGE_URL,
    afterimages: [
      {
        id: 1001,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_processed_image_1.jpg",
      },
      {
        id: 1002,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_processed_image_2.jpg",
      },
    ],
    message: "処理が完了しました（モック）",
  },

  "/item-removal/clear/": {
    status: "success",
    message: "画像がクリアされました（モック）",
  },

  "/item-removal/regen/": {
    id: 123,
    status: "success",
    processed_image_url: MOCK_IMAGE_URL,
    image_url: MOCK_IMAGE_URL,
    file: MOCK_IMAGE_URL,
    afterimages: [
      {
        id: 2001,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_regenerated_image_1.jpg",
      },
      {
        id: 2002,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_regenerated_image_2.jpg",
      },
    ],
    message: "画像が再実行されました（モック）",
  },

  // 家具画像追加
  "/item-removal/add-images/": {
    id: 789,
    status: "success",
    processed_image_url: MOCK_IMAGE_URL,
    image_url: MOCK_IMAGE_URL,
    file: MOCK_IMAGE_URL,
    afterimages: [
      {
        id: 3001,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_added_image_1.jpg",
      },
      {
        id: 3002,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_added_image_2.jpg",
      },
    ],
    message: "画像が追加されました（モック）",
  },

  // ルームデザイン関連
  "/api/room-design/firstgen-after-imagen/": {
    id: 456,
    status: "success",
    processed_image_url: MOCK_IMAGE_URL,
    image_url: MOCK_IMAGE_URL,
    file: MOCK_IMAGE_URL,
    processedimages: [
      {
        id: 4001,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_room_design_1.jpg",
      },
      {
        id: 4002,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_room_design_2.jpg",
      },
    ],
    message: "ルームデザインが生成されました（モック）",
  },

  "/api/room-design/clear-after-images/": {
    status: "success",
    message: "画像がクリアされました（モック）",
  },

  "/api/room-design/regen-after-images/": {
    id: 456,
    status: "success",
    processed_image_url: MOCK_IMAGE_URL,
    image_url: MOCK_IMAGE_URL,
    file: MOCK_IMAGE_URL,
    processedimages: [
      {
        id: 5001,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_regenerated_room_1.jpg",
      },
      {
        id: 5002,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_regenerated_room_2.jpg",
      },
    ],
    message: "画像が再実行されました（モック）",
  },
};

/**
 * URLパターンに基づいてモックレスポンスを取得する
 * 完全一致するパスがない場合は、パターンマッチングを試みる
 */
export const getMockResponse = (
  url: string,
  method: string,
  data?: any,
): any => {
  // 完全一致するパスがあればそれを返す
  if (mockResponses[url]) {
    return mockResponses[url];
  }

  // IDを含むパスなど、パターンマッチングを試みる
  for (const path in mockResponses) {
    // パスの最後にIDがある場合（例: /api/users/1/）
    if (url.match(new RegExp(`^${path}\\d+\\/?$`))) {
      return mockResponses[path];
    }

    // パスの途中にIDがある場合（例: /item-removal/clear/1/）
    if (path.endsWith("/") && url.includes(path)) {
      return mockResponses[path];
    }
  }

  // マッチするものがなければデフォルトのレスポンスを返す
  return {
    status: "success",
    message: "モックレスポンス（デフォルト）",
    processed_image_url: MOCK_IMAGE_URL,
    image_url: MOCK_IMAGE_URL,
    file: MOCK_IMAGE_URL,
    id: 999,
    afterimages: [
      {
        id: 9001,
        downloadurl: MOCK_IMAGE_URL,
        file_name: "mock_default_image.jpg",
      },
    ],
  };
};
